import { useCallback, useEffect, useState } from 'react';

import { useStoreContext } from 'features/store';
import { isEmpty } from 'lodash-es';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPaymentMethod, LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { usePrevious } from 'hooks/use-previous';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { updateAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { IAppliedRewards, IUseLoyaltyRewards, UseLoyaltyRewards } from 'state/loyalty/hooks/types';
import { useServiceModeContext } from 'state/service-mode';

import { LoyaltyAppliedOffer } from '../types';

import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentive-evaluation';
import { parseEntry } from './utils/incentive-evaluation';

export const useLoyaltyRewards: UseLoyaltyRewards = (loyaltyUser): IUseLoyaltyRewards => {
  const { refetchRewards, engineRewardsMap } = useLoyaltyRewardsList();
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();

  const [shouldApplyStorageRewards, setShouldApplyStorageRewards] = useState(false);
  const dispatch = useAppDispatch();
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );

  const loyaltyUserId = loyaltyUser?.id;
  const storeId = store?.number;
  const previousLoyaltyUserId = usePrevious(loyaltyUserId);

  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();

  const evaluateLoyaltyUserRewards = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyRewards: IAppliedRewards | null,
      cartEntries?: ILegacyCartEntry[],
      appliedLoyaltyOffers?: LoyaltyAppliedOffer[],
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const parsedEntries = cartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.REWARD,
          incentivesToExclude: appliedLoyaltyOffers,
        }),
        []
      );

      const evaluationResult = await evaluateLoyaltyIncentives({
        loyaltyId,
        appliedIncentives: appliedLoyaltyRewards || {},
        incentiveType: LoyaltyPromotionType.REWARD,
        cartEntries: parsedEntries,
        subtotalAmount,
        paymentMethod,
        serviceMode,
        storeId,
      });

      if (evaluationResult) {
        dispatch(actions.loyalty.setRewardsFeedbackMap(evaluationResult));
      }
    },
    [evaluateLoyaltyIncentives, serviceMode, storeId, dispatch]
  );

  useEffect(() => {
    // loyalty user id will populate upon logging in / initial page load
    if (!previousLoyaltyUserId && loyaltyUserId) {
      setShouldApplyStorageRewards(true);
    }
    // occurs when a user logs out
    else if (previousLoyaltyUserId && !loyaltyUserId) {
      updateAppliedRewardsInStorage({});
    }
  }, [previousLoyaltyUserId, loyaltyUserId]);

  useEffect(() => {
    if (!isEmpty(availableLoyaltyRewardsMap) && shouldApplyStorageRewards) {
      dispatch(actions.loyalty.rehydrateAppliedReward());
      setShouldApplyStorageRewards(false);
    }
  }, [availableLoyaltyRewardsMap, dispatch, shouldApplyStorageRewards]);

  return { refetchRewards, engineRewardsMap, evaluateLoyaltyUserRewards };
};
